/**
 * AudioChoreographer — AAA audio mixing and sequencing layer.
 *
 * Sits on top of AudioManager. Responsibilities:
 *   1. Sidechain-style music ducking on impactful SFX
 *   2. Score tick-up with per-digit sound
 *   3. Stage-clear orchestrated reveal (star → score → bonus → CTA)
 *   4. Combo escalation + milestone stingers
 *   5. Voice grunt dispatch (random selection, cooldown-gated)
 *
 * Usage:
 *   AudioChoreographer.get(scene).duck(0.3, 250);
 *   AudioChoreographer.get(scene).tickUpScore(from, to, displayText, durationMs);
 *   AudioChoreographer.get(scene).playVoice('shout');
 */

import Phaser from 'phaser';
import { AudioManager } from './AudioManager';

type VoiceTag = 'shout' | 'ow' | 'laugh' | 'victory' | 'angry' | 'charge';

export class AudioChoreographer {
  private static instances: WeakMap<Phaser.Scene, AudioChoreographer> = new WeakMap();

  static get(scene: Phaser.Scene): AudioChoreographer {
    let inst = this.instances.get(scene);
    if (!inst) {
      inst = new AudioChoreographer(scene);
      this.instances.set(scene, inst);
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.instances.delete(scene);
      });
    }
    return inst;
  }

  private scene: Phaser.Scene;
  private lastVoiceTime = 0;
  private voiceCooldown = 1500; // ms between voice lines
  private lastComboMilestone = 0;
  private duckTween?: Phaser.Tweens.Tween;

  private constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ─── Sidechain-style music ducking ─────────────────────────────────────────

  /**
   * Duck the current music by `amount` (0..1) for `durationMs`, then recover.
   * Amount 0.3 = music drops to 30% of its current volume.
   */
  duck(amount = 0.35, durationMs = 250, recoverMs = 350): void {
    const audio = AudioManager.getInstance();
    const music: any = (audio as any).currentMusic;
    if (!music || typeof music.volume !== 'number') return;

    const settings = audio.getMixerSettings();
    const base = settings.music.muted ? 0 : settings.music.volume;
    const duckVolume = Math.max(0, base * amount);

    // Kill any existing duck tween
    if (this.duckTween) this.duckTween.stop();

    // Fast drop
    music.volume = duckVolume;

    // Gradual recover
    this.duckTween = this.scene.tweens.add({
      targets: music,
      volume: base,
      duration: recoverMs,
      delay: durationMs,
      ease: 'Sine.easeOut',
    });
  }

  // ─── Score tick-up ─────────────────────────────────────────────────────────

  /**
   * Animate a score number rolling from `from` to `to` over `duration` ms,
   * writing each step into `onTick(value)` and playing a subtle tick SFX.
   */
  tickUpScore(
    from: number,
    to: number,
    onTick: (value: number) => void,
    durationMs = 800,
  ): void {
    const delta = to - from;
    if (delta === 0) {
      onTick(to);
      return;
    }

    const steps = Math.min(30, Math.max(10, Math.abs(Math.floor(delta / 20))));
    const stepDuration = durationMs / steps;
    const audio = AudioManager.getInstance();
    let current = 0;

    const timer = this.scene.time.addEvent({
      delay: stepDuration,
      repeat: steps - 1,
      callback: () => {
        current++;
        const t = current / steps;
        // Ease-out quad for nice deceleration
        const eased = 1 - (1 - t) * (1 - t);
        const value = Math.round(from + delta * eased);
        onTick(value);
        // Tick SFX with ascending pitch for satisfaction
        if (current % 3 === 0) {
          try {
            audio.playSFX(this.scene, 'sfx_score_tick', {
              volume: 0.25,
              detune: current * 15,
            } as any);
          } catch {}
        }
      },
    });

    // Ensure final value is set exactly (timer may round off)
    this.scene.time.delayedCall(durationMs + 20, () => {
      onTick(to);
      timer.remove();
    });
  }

  // ─── Combo milestones ──────────────────────────────────────────────────────

  /**
   * Play a combo milestone stinger if the combo count crosses a threshold.
   * Returns the milestone name if one was played, else null.
   */
  checkComboMilestone(comboCount: number): string | null {
    const milestones = [
      { count: 20, key: 'stinger_combo_20', name: 'GODLIKE' },
      { count: 10, key: 'stinger_combo_10', name: 'PERFECT' },
      { count: 5,  key: 'stinger_combo_5',  name: 'GREAT' },
      { count: 3,  key: 'stinger_combo_3',  name: 'COMBO' },
    ];
    const hit = milestones.find(m => comboCount === m.count);
    if (!hit) return null;
    if (hit.count <= this.lastComboMilestone) return null;
    this.lastComboMilestone = hit.count;

    const audio = AudioManager.getInstance();
    try {
      audio.playSFX(this.scene, hit.key, { volume: 0.7 });
    } catch {
      // Fallback to generic combo sound
      try { audio.playSFX(this.scene, 'sfx_combo', { volume: 0.7 }); } catch {}
    }
    // Duck music for drama
    this.duck(0.35, 200, 400);
    return hit.name;
  }

  resetComboMilestone(): void {
    this.lastComboMilestone = 0;
  }

  // ─── Voice grunts ──────────────────────────────────────────────────────────

  /**
   * Play a random voice grunt for the given tag, respecting cooldown.
   * Returns true if a line was played.
   *
   * 'victory' is routed through the Oceanic Overwatch Clyde voice pool via
   * AudioManager.playVoiceStinger — the old voice_victory.mp3 was a burpy
   * low-freq "Raaah!" roar that ruined the stage-clear transition.
   */
  playVoice(tag: VoiceTag): boolean {
    const now = this.scene.time.now;
    if (now - this.lastVoiceTime < this.voiceCooldown) return false;

    const audio = AudioManager.getInstance();

    // Victory → clean Clyde pool (shared cooldown with StageClearScene's stinger
    // so we never double up the voice)
    if (tag === 'victory') {
      audio.playVoiceStinger(this.scene, 'stage_clear', 1500);
      this.lastVoiceTime = now;
      this.duck(0.55, 300, 400);
      return true;
    }

    const key = this.pickVoiceKey(tag);
    if (!key) return false;
    if (!this.scene.cache.audio.exists(key)) return false;

    try {
      audio.playSFX(this.scene, key, { volume: 0.75 });
    } catch {
      return false;
    }
    this.lastVoiceTime = now;
    this.duck(0.55, 300, 400);
    return true;
  }

  private pickVoiceKey(tag: VoiceTag): string | null {
    switch (tag) {
      case 'shout': {
        const n = Math.random() < 0.5 ? 1 : 2;
        return `voice_shout_${n}`;
      }
      case 'ow':      return 'voice_ow';
      case 'laugh':   return 'voice_laugh';
      case 'victory': return null; // handled above via Clyde pool
      case 'angry':   return 'voice_angry';
      case 'charge':  return 'voice_charge';
      default:        return null;
    }
  }

  // ─── Stage clear orchestration ─────────────────────────────────────────────

  /**
   * Fire a "music swell" — briefly raise the music volume to reward a moment.
   */
  musicSwell(amount = 1.15, durationMs = 400): void {
    const audio = AudioManager.getInstance();
    const music: any = (audio as any).currentMusic;
    if (!music || typeof music.volume !== 'number') return;

    const settings = audio.getMixerSettings();
    const base = settings.music.muted ? 0 : settings.music.volume;
    const swellVolume = Math.min(1, base * amount);

    this.scene.tweens.add({
      targets: music,
      volume: swellVolume,
      duration: durationMs / 2,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        music.volume = base;
      },
    });
  }
}
